import { type CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { filterBy } from '@progress/kendo-react-data-tools'
import {
  DropDownTree,
  type DropDownTreeHandle,
  type DropDownTreeProps,
} from '@progress/kendo-react-dropdowns'
import { processTreeViewItems } from '@progress/kendo-react-treeview'
import React from 'react'
import { useSet } from 'react-use'
import * as R from 'remeda'

import { Typography } from '~/components/elements/Typography'
import { type Question as QuestionBase } from '~/gql/graphql'
import {
  compact,
  exists,
  QuestionOp,
  type Unassigned,
  unassignedSymbol,
} from '~/utils'

import styles from './QuestionTreeSelector.css'

type Question = App.PickGraphQL<
  QuestionBase,
  'mt_questions' | 'question_id' | 'question_text'
>

type QuestionWithItems<TQuestion extends Question> = TQuestion & {
  items: Omit<TQuestion, 'mt_questions'>[] | null
}

const item = <TQuestion extends Question>({ item }: { item: TQuestion }) => (
  <Typography color="inherit" variant="normal">
    {typeof item.question_id === 'string'
      ? QuestionOp.toText(item)
      : item.question_text}
  </Typography>
)

const valueHolder = <TQuestion extends Question>({
  item,
}: {
  item?: TQuestion
}) => (
  <Typography variant="normal">
    {typeof item?.question_id === 'string'
      ? QuestionOp.toText(item)
      : item?.question_text}
  </Typography>
)

export type QuestionTreeSelectorProps<TQuestion extends Question> = {
  'data-testid'?: string
  isMatrixQuestionSelectable?: boolean
  isNullSelectable?: boolean
  nullText?: string
  onBlur?: DropDownTreeProps['onBlur']
  onChange?: (questionId: TQuestion['question_id']) => void
  questions: TQuestion[]
  unsetText?: string
  value: TQuestion['question_id'] | Unassigned | null
}

export const QuestionTreeSelector = React.forwardRef(
  <TQuestion extends Question>(
    {
      'data-testid': dataTestId = 'components-features-question-tree-selector',
      isMatrixQuestionSelectable = false,
      isNullSelectable = false,
      nullText,
      onBlur,
      onChange,
      questions,
      unsetText,
      value: valueProp,
    }: QuestionTreeSelectorProps<TQuestion>,
    ref: React.ForwardedRef<DropDownTreeHandle>,
  ) => {
    const value = React.useMemo(
      () =>
        valueProp === null
          ? { question_id: null, question_text: nullText }
          : (questions.find(({ question_id }) => question_id === valueProp) ?? {
              question_id: unassignedSymbol,
              question_text: unsetText,
            }),
      [valueProp, nullText, questions, unsetText],
    )

    const [opened, setOpened] = React.useState(false)
    const [expandIdSet, { toggle: toggleExpandIdSet }] = useSet<string>(
      new Set(),
    )
    const [filter, setFilter] = React.useState('')

    const data = React.useMemo(() => {
      const questionsWithItems: QuestionWithItems<TQuestion>[] = questions.map(
        (question) => {
          if (question.mt_questions.length === 0) {
            return { ...question, items: null }
          }

          const items = compact([
            isMatrixQuestionSelectable && question,
            ...questions.filter(({ question_id }) =>
              question.mt_questions.includes(question_id),
            ),
          ]).map(({ mt_questions: _mt_questions, ...rest }) => rest)

          return { ...question, items }
        },
      )
      const questionsWithItemsWithNull = isNullSelectable
        ? [
            { question_id: null, question_text: nullText, items: null },
            ...questionsWithItems,
          ]
        : questionsWithItems

      const itemQuestionIds = questions.flatMap(R.prop('mt_questions'))

      const treeQuestions = R.differenceWith(
        questionsWithItemsWithNull,
        itemQuestionIds,
        (question, question_id) => question.question_id === question_id,
      )

      const processedTreeQuestions = processTreeViewItems(treeQuestions, {
        expand: { ids: [...expandIdSet], idField: 'question_id' },
      })

      const filterDescriptor: CompositeFilterDescriptor = {
        logic: 'or',
        filters: ['question_id', 'question_text'].map((filterField) => ({
          field: filterField,
          operator: 'contains',
          value: filter,
          ignoreCase: true,
        })),
      }

      return filterBy(processedTreeQuestions, [filterDescriptor], 'items')
    }, [
      expandIdSet,
      filter,
      isMatrixQuestionSelectable,
      isNullSelectable,
      nullText,
      questions,
    ])

    const handleBlur = React.useCallback<
      EH<DropDownTreeProps, 'onExpandChange'>
    >(
      (event) => {
        setOpened(false)
        onBlur?.(event)
      },
      [onBlur],
    )

    const handleChange = React.useCallback<EH<DropDownTreeProps, 'onChange'>>(
      (event) => {
        const questionsWithItem: QuestionWithItems<TQuestion> = event.value
        if (exists(questionsWithItem.items)) {
          toggleExpandIdSet(questionsWithItem.question_id)
        } else {
          onChange?.(questionsWithItem.question_id)
          setOpened(false)
        }
      },
      [onChange, toggleExpandIdSet],
    )
    const handleExpandChange = React.useCallback<
      EH<DropDownTreeProps, 'onExpandChange'>
    >(
      (event) => {
        toggleExpandIdSet(event.item.question_id)
      },
      [toggleExpandIdSet],
    )

    const handleClose = React.useCallback<
      EH<DropDownTreeProps, 'onClose'>
    >(() => {
      setFilter('')
    }, [])

    const handleFilterChange = React.useCallback<
      EH<DropDownTreeProps, 'onFilterChange'>
    >((event) => {
      setFilter(event.filter.value)
    }, [])

    const handleOpen = React.useCallback<
      EH<DropDownTreeProps, 'onExpandChange'>
    >(() => {
      setOpened(true)
    }, [])

    return (
      <DropDownTree
        ref={ref}
        className={styles.root}
        data={data}
        data-testid={dataTestId}
        dataItemKey="question_id"
        filter={filter}
        filterable={true}
        item={item}
        opened={opened}
        popupSettings={{ popupClass: styles.popup }}
        subItemsField="items"
        textField="question_text"
        value={value}
        valueHolder={valueHolder}
        onBlur={handleBlur}
        onChange={handleChange}
        onClose={handleClose}
        onExpandChange={handleExpandChange}
        onFilterChange={handleFilterChange}
        onOpen={handleOpen}
      />
    )
  },
)
